/**
 * Monkey patch for TF.js WebGPU TransposeProgram WGSL type mismatch bug
 *
 * Chrome 141+ has stricter WGSL type checking after the Tint IR rewrite.
 * TF.js TransposeProgram passes i32 where f32 is expected in setOutputAtIndex.
 *
 * This patch intercepts shader compilation and fixes the type mismatch.
 *
 * Issue: https://github.com/tensorflow/tfjs/issues/8638
 */

let isPatched = false;
let patchedCount = 0;

/**
 * Apply the WebGPU shader patch for TF.js type mismatch bugs
 */
export function applyWebGPUShaderPatch() {
  if (isPatched) {
    console.log('[WebGPU Patch] Already applied');
    return;
  }

  if (typeof navigator === 'undefined' || !navigator.gpu) {
    console.log('[WebGPU Patch] WebGPU not available, skipping patch');
    return;
  }

  // We need to patch after getting a device, so we patch requestDevice
  const originalRequestAdapter = navigator.gpu.requestAdapter.bind(navigator.gpu);

  navigator.gpu.requestAdapter = async function(...args) {
    const adapter = await originalRequestAdapter(...args);
    if (!adapter) return adapter;

    const originalRequestDevice = adapter.requestDevice.bind(adapter);

    adapter.requestDevice = async function(...deviceArgs) {
      const device = await originalRequestDevice(...deviceArgs);
      if (!device) return device;

      // Patch createShaderModule on this device
      patchDevice(device);
      return device;
    };

    return adapter;
  };

  isPatched = true;
  console.log('[WebGPU Patch] Installed shader patch for TF.js TransposeProgram bug');
}

function patchDevice(device) {
  if (device._shaderPatchApplied) return;

  const originalCreateShaderModule = device.createShaderModule.bind(device);

  device.createShaderModule = function(descriptor) {
    if (descriptor && descriptor.code) {
      const fixedCode = fixWGSLTypeMismatches(descriptor.code, descriptor.label);
      if (fixedCode !== descriptor.code) {
        descriptor = { ...descriptor, code: fixedCode };
      }
    }
    return originalCreateShaderModule(descriptor);
  };

  device._shaderPatchApplied = true;
}

/**
 * Fix WGSL type mismatches in TF.js shaders
 */
function fixWGSLTypeMismatches(code, label) {
  let fixed = code;
  let wasPatched = false;

  // Fix TransposeProgram: setOutputAtIndex expects f32, but A[...] returns i32 for int tensors
  // Pattern: setOutputAtIndex(flatIndex, A[getIndexFromCoords...])
  // Fix: wrap with f32()
  const transposePattern = /(setOutputAtIndex\s*\(\s*\w+\s*,\s*)(A\s*\[\s*getIndexFromCoords\d+D\s*\([^)]+\)\s*\])/g;

  if (transposePattern.test(fixed)) {
    fixed = fixed.replace(transposePattern, (match, prefix, arrayAccess) => {
      // Check if already wrapped with f32()
      if (match.includes('f32(')) return match;
      return `${prefix}f32(${arrayAccess})`;
    });
    wasPatched = true;
  }

  // More general fix: any setOutputAtIndex where second arg is array access that might be i32
  // Pattern: setOutputAtIndex(..., X[...]) where X is a tensor
  const generalPattern = /(setOutputAtIndex\s*\(\s*[^,]+\s*,\s*)([A-Z]\s*\[[^\]]+\])(\s*\))/g;

  if (generalPattern.test(fixed) && !wasPatched) {
    fixed = fixed.replace(generalPattern, (match, prefix, arrayAccess, suffix) => {
      // Check if already wrapped with f32()
      if (prefix.includes('f32(') || arrayAccess.startsWith('f32(')) return match;
      return `${prefix}f32(${arrayAccess})${suffix}`;
    });
    wasPatched = true;
  }

  if (wasPatched) {
    patchedCount++;
    if (patchedCount <= 5 || patchedCount % 100 === 0) {
      console.log(`[WebGPU Patch] Fixed type mismatch in shader: ${label || 'unnamed'} (total: ${patchedCount})`);
    }
  }

  return fixed;
}

/**
 * Get patch statistics
 */
export function getWebGPUPatchStats() {
  return {
    isPatched,
    patchedCount
  };
}

export default applyWebGPUShaderPatch;
